/** Dashboard charts - pure SVG, same geometry as the approved mockup. */
import { TYPE_LABELS } from "../lib/brand";

export function Spark({ vals, color }: { vals: number[]; color: string }) {
  const w = 92, h = 30;
  const v = vals.length > 1 ? vals : [0, ...(vals.length ? vals : [0])];
  const mx = Math.max(...v), mn = Math.min(...v);
  const pts = v.map((x, i) => [i * (w / (v.length - 1)), h - 3 - ((x - mn) / (mx - mn || 1)) * (h - 6)] as const);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const id = `sg${color.slice(1)}`;
  const last = pts[pts.length - 1]!;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity=".28" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L${w} ${h} L0 ${h}Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />
    </svg>
  );
}

export interface Seg { l: string; v: number; c: string }
export function Donut({ segs, center, sub }: { segs: Seg[]; center: number | string; sub: string }) {
  const R = 54, C = 2 * Math.PI * R, tot = segs.reduce((a, b) => a + b.v, 0) || 1;
  let off = 0;
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" role="img" aria-label={sub}>
      <circle r={R} cx="70" cy="70" fill="none" stroke="#EEF1F5" strokeWidth="18" />
      {segs.filter((s) => s.v > 0).map((s) => {
        const len = (C * s.v) / tot;
        const el = (
          <circle key={s.l} r={R} cx="70" cy="70" fill="none" stroke={s.c} strokeWidth="18"
            strokeDasharray={`${Math.max(len - 2, 0)} ${C}`} strokeDashoffset={-off} transform="rotate(-90 70 70)">
            <title>{`${s.l}: ${s.v}`}</title>
          </circle>
        );
        off += len;
        return el;
      })}
      <text x="70" y="68" textAnchor="middle" fontSize="26" fontWeight="800" fill="#131925" style={{ fontFamily: "var(--display)" }}>{center}</text>
      <text x="70" y="86" textAnchor="middle" fontSize="10.5" fill="#6F7681">{sub}</text>
    </svg>
  );
}

export function Gauge({ pct, label, color, valText }: { pct: number; label: string; color: string; valText: string }) {
  const r = 48, cx = 60, cy = 58;
  const a = Math.PI * (1 - Math.max(0, Math.min(1, pct)));
  const x = cx + r * Math.cos(a), y = cy - r * Math.sin(a);
  return (
    <svg width="120" height="76" viewBox="0 0 120 76" role="img" aria-label={`${label}: ${valText}`}>
      <path d={`M${cx - r} ${cy} A${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#EEF1F5" strokeWidth="11" strokeLinecap="round" />
      {pct > 0 && <path d={`M${cx - r} ${cy} A${r} ${r} 0 0 1 ${x.toFixed(1)} ${y.toFixed(1)}`} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round" />}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="19" fontWeight="800" fill="#131925" style={{ fontFamily: "var(--display)" }}>{valText}</text>
    </svg>
  );
}

export function StackedBars({ groups, keys, colors }: { groups: { label: string; v: Record<string, number> }[]; keys: string[]; colors: Record<string, string> }) {
  const W = 330, H = 170, pad = 26;
  const n = Math.max(groups.length, 1);
  const bw = Math.min(38, (W - pad) / n - 14);
  const mx = Math.max(1, ...groups.map((g) => keys.reduce((a, k) => a + (g.v[k] || 0), 0)));
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 28}`} role="img" aria-label="Documents by company">
      {(mx % 2 === 0 ? [0, 0.5, 1] : [0, 1]).map((t) => {
        const y = H - (H - 14) * t;
        return (
          <g key={t}>
            <line x1={pad} x2={W} y1={y} y2={y} stroke="#EEF1F5" />
            <text x={pad - 6} y={y + 3} textAnchor="end" fontSize="9.5" fill="#9AA1AB">{Math.round(mx * t)}</text>
          </g>
        );
      })}
      {groups.map((g, i) => {
        const x = pad + 12 + i * ((W - pad - 12) / n);
        let y = H;
        const tot = keys.reduce((a, k) => a + (g.v[k] || 0), 0);
        return (
          <g key={g.label}>
            {keys.map((k) => {
              const v = g.v[k] || 0;
              if (!v) return null;
              const h = ((H - 14) * v) / mx;
              y -= h;
              return (
                <rect key={k} x={x} y={y} width={bw} height={Math.max(h - 1.5, 0)} rx="3" fill={colors[k]}>
                  <title>{`${g.label} · ${TYPE_LABELS[k as keyof typeof TYPE_LABELS] ?? k}: ${v}`}</title>
                </rect>
              );
            })}
            <text x={x + bw / 2} y={y - 5} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#131925">{tot}</text>
            <text x={x + bw / 2} y={H + 16} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#545E6D">{g.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function Columns({ items }: { items: { l: string; v: number; c: string; lc?: string }[] }) {
  const W = 330, H = 150, pad = 20, n = items.length, bw = (W - pad) / n - 12;
  const mx = Math.max(1, ...items.map((i) => i.v));
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 30}`} role="img" aria-label="Reviews due by month">
      {items.map((it, i) => {
        const x = pad + i * ((W - pad) / n);
        const h = ((H - 18) * it.v) / mx;
        return (
          <g key={it.l}>
            <rect x={x} y={H - h} width={bw} height={Math.max(h, 2)} rx="5" fill={it.c} opacity={it.v ? 1 : 0.25}><title>{`${it.l}: ${it.v}`}</title></rect>
            <text x={x + bw / 2} y={H - h - 6} textAnchor="middle" fontSize="11" fontWeight="700" fill="#131925">{it.v || ""}</text>
            <text x={x + bw / 2} y={H + 16} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={it.lc || "#545E6D"}>{it.l}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function HBars({ items, labelW = 120, onPick }: { items: { l: string; v: number; c: string; k?: string }[]; labelW?: number; onPick?: (k: string) => void }) {
  const mx = Math.max(1, ...items.map((i) => i.v));
  return (
    <div style={{ display: "grid", gap: 9 }}>
      {items.map((it) => {
        const click = onPick && it.k ? () => onPick(it.k!) : undefined;
        return (
          <div key={it.l} className={click ? "hbar-pick" : undefined} onClick={click} role={click ? "button" : undefined} tabIndex={click ? 0 : undefined}
            onKeyDown={click ? (e) => { if (e.key === "Enter") click(); } : undefined} title={click ? `Open ${it.l} in the Library` : undefined}
            style={{ display: "grid", gridTemplateColumns: `${labelW}px 1fr 34px`, gap: 8, alignItems: "center", fontSize: 12.5 }}>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--text)" }}>{it.l}</span>
            <span style={{ height: 9, borderRadius: 9, background: "#EEF1F5", overflow: "hidden" }}>
              <span style={{ display: "block", height: "100%", width: `${Math.round((100 * it.v) / mx)}%`, background: `linear-gradient(90deg,${it.c},color-mix(in srgb,${it.c} 60%,#fff))`, borderRadius: 9 }} />
            </span>
            <b style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink)", textAlign: "right" }}>{it.v}</b>
          </div>
        );
      })}
    </div>
  );
}
